import { useCallback } from 'react';

import { OasMemoryError, OasError } from 'core/atoms/errors';

import { gql, useMutation } from 'core/dna/types/apollo';
import { CreateSubstituteTaskInput } from 'core/dna/types/generated/_globalTypes';

import {
  CreateSubstituteTask,
  CreateSubstituteTaskVariables,
} from 'core/dna/types/generated/CreateSubstituteTask';

import { SUBSTITUTE_TASK_DATA_FRAGMENT } from 'modules/substitute/memory/apollo/_fragments';
import { GET_SUBSTITUTE_JOBS_QUERY } from 'modules/substitute/memory/apollo/substitute-jobs/remote';

export const CREATE_SUBSTITUTE_TASK_MUTATION = gql`
  mutation CreateSubstituteTask($input: CreateSubstituteTaskInput!) {
    createSubstituteTask(input: $input) {
      ...SubstituteTaskData
    }
  }
  ${SUBSTITUTE_TASK_DATA_FRAGMENT}
`;

const TITLE = 'use-create-substitute-task';

export const useCreateSubstituteTask = () => {
  const [createSubstituteTask] = useMutation<
    CreateSubstituteTask,
    CreateSubstituteTaskVariables
  >(CREATE_SUBSTITUTE_TASK_MUTATION);

  const func = useCallback(
    async (input: CreateSubstituteTaskInput) => {
      try {
        const result = await createSubstituteTask({
          variables: {
            input,
          },
          refetchQueries: [
            {
              query: GET_SUBSTITUTE_JOBS_QUERY,
            },
          ],
        });

        if (result.errors) {
          throw new OasMemoryError(JSON.stringify(result.errors), {
            title: TITLE,
          });
        }
        return result.data?.createSubstituteTask ?? undefined;
      } catch (e) {
        if (e instanceof OasError) {
          throw e;
        } else {
          throw OasMemoryError.fromError(e, {
            title: TITLE,
          });
        }
      }
    },
    [createSubstituteTask],
  );

  return {
    createSubstituteTask: func,
  };
};
